import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { linkFamilyAPI } from "../../../services/linkFamilyService";

const RELATIONSHIP_TYPES = [
  { value: "Parent", label: "Parent" },
  { value: "Spouse", label: "Spouse" },
  { value: "Child", label: "Child" },
];

const RELATION_DETAILS = {
  Parent: {
    summary: "Link an existing parent (or parent couple) to a child who already exists in the tree.",
    person1Description: "Choose the parent profile (couple) you want to reuse.",
    person2Description: "Select the child who currently does not have parents assigned.",
    confirmation: (parentLabel, childLabel) =>
      `I confirm ${parentLabel} is the parent of ${childLabel}.`,
  },
  Child: {
    summary: "Link an existing child profile to the correct parents already in the system.",
    person1Description: "Select the child who is missing parent information.",
    person2Description: "Choose the parents (couple) this child should belong to.",
    confirmation: (childLabel, parentLabel) =>
      `I confirm ${childLabel} is the child of ${parentLabel}.`,
  },
  Spouse: {
    summary: "Link two existing members together as spouses.",
    person1Description: "Pick the first spouse profile without an assigned partner.",
    person2Description: "Pick the second spouse profile (must differ from the first).",
    confirmation: (spouse1Label, spouse2Label) =>
      `I confirm ${spouse1Label} and ${spouse2Label} are spouses in this family tree.`,
    fallback: "I confirm the selected person and the linked member are spouses in this family tree.",
  },
};

const RELATION_PAYLOAD_MAP = {
  Parent: "parent",
  Child: "child",
  Spouse: "spouse",
};

const normalizeId = (value) => {
  if (value === undefined || value === null) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lower = str.toLowerCase();
  if (lower === "null" || lower === "undefined") return "";
  return str;
};

const getParentIdValue = (member) =>
  normalizeId(
    member?.parentId ??
    member?.parent_id ??
    member?.parentid_c ??
    member?.parent_record_id ??
    member?.parentID
  );

const getSpouseIdValue = (member) =>
  normalizeId(
    member?.spouseId ??
    member?.spouse_id ??
    member?.spouseid_c ??
    member?.spouseID
  );

const inferGender = (member) => {
  const raw = String(member?.gender ?? member?.gender_c ?? "").trim().toLowerCase();
  if (raw === "male" || raw === "female") return raw;
  return "";
};

const getMemberLabel = (member) =>
  member?.name || member?.firstName || member?.fullName || member?.id || "Unnamed";

const buildGreatLabel = (labelA, labelB) => `${labelA} & ${labelB}`;

const buildCoupleOptions = (members = []) => {
  const byId = new Map();
  members.forEach((m) => {
    const id = normalizeId(m?.id);
    if (id) byId.set(id, m);
  });
  const couples = new Map();
  members.forEach((person) => {
    const myId = normalizeId(person?.id);
    const spouseId = getSpouseIdValue(person);
    if (!myId || !spouseId) return;
    const spouse = byId.get(spouseId);
    if (!spouse) return;
    const key = myId < spouseId ? `${myId}_${spouseId}` : `${spouseId}_${myId}`;
    if (couples.has(key)) return;
    const genderA = inferGender(person);
    const genderB = inferGender(spouse);
    const male = genderA === "male" ? person : genderB === "male" ? spouse : person;
    const female = male === person ? spouse : person;
    couples.set(key, {
      valueId: normalizeId(male?.id) || normalizeId(person?.id) || normalizeId(spouse?.id),
      label: buildGreatLabel(getMemberLabel(male), getMemberLabel(female)),
      maleId: normalizeId(male?.id),
      femaleId: normalizeId(female?.id),
      persons: [male, female],
    });
  });
  return Array.from(couples.values());
};

const buildPersonOptions = (members = [], filterFn = () => true) =>
  members
    .filter((member) => {
      const id = normalizeId(member?.id);
      return Boolean(id) && filterFn(member);
    })
    .map((member) => ({
      valueId: normalizeId(member.id),
      label: getMemberLabel(member),
      person: member,
    }));

function ExistingMemberLinkDialog({ open, onClose, members = [], onLinkSuccess }) {
  const [relationshipType, setRelationshipType] = useState("Parent");
  const [person1, setPerson1] = useState(null);
  const [person2, setPerson2] = useState(null);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const FIELD_SX = useMemo(
    () => ({
      "& .MuiInputBase-root": {
        height: "48px",
      },
      "& .MuiOutlinedInput-input": {
        padding: "0 12px",
        fontSize: "0.9rem",
      },
      "& .MuiOutlinedInput-input::placeholder": {
        fontSize: "0.9rem",
        opacity: 0.8,
      },
    }),
    []
  );

  useEffect(() => {
    if (!open) {
      setRelationshipType("Parent");
      setPerson1(null);
      setPerson2(null);
      setConfirmationChecked(false);
      setAttemptedSubmit(false);
      setSubmitting(false);
      setApiError("");
    }
  }, [open]);

  useEffect(() => {
    setPerson1(null);
    setPerson2(null);
    setConfirmationChecked(false);
    setAttemptedSubmit(false);
    setApiError("");
  }, [relationshipType]);

  useEffect(() => {
    setConfirmationChecked(false);
    setAttemptedSubmit(false);
    setApiError("");
  }, [person1, person2]);

  useEffect(() => {
    if (relationshipType !== "Spouse") return;
    if (!person1 && !person2) return;

    if (person1 && person2) {
      const requiredForPerson2 = getOppositeGender(person1);
      const person2Gender = inferGender(person2.person);
      if (requiredForPerson2 && person2Gender && person2Gender !== requiredForPerson2) {
        setPerson2(null);
        return;
      }

      const requiredForPerson1 = getOppositeGender(person2);
      const person1Gender = inferGender(person1.person);
      if (requiredForPerson1 && person1Gender && person1Gender !== requiredForPerson1) {
        setPerson1(null);
      }
    }
  }, [relationshipType, person1, person2]);

  const normalizedMembers = useMemo(() => (Array.isArray(members) ? members : []), [members]);

  const parentlessOptions = useMemo(
    () => buildPersonOptions(normalizedMembers, (member) => !getParentIdValue(member)),
    [normalizedMembers]
  );

  const spouseCandidates = useMemo(
    () => buildPersonOptions(normalizedMembers, (member) => !getSpouseIdValue(member)),
    [normalizedMembers]
  );

  const getOppositeGender = (option) => {
    const gender = inferGender(option?.person || option);
    if (gender === "male") return "female";
    if (gender === "female") return "male";
    return "";
  };

  const coupleOptions = useMemo(
    () => buildCoupleOptions(normalizedMembers),
    [normalizedMembers]
  );

  const spouseOptionsForPerson1 = useMemo(() => {
    if (!person2) return spouseCandidates;
    const disallowedId = person2.valueId;
    const requiredGender = getOppositeGender(person2);
    return spouseCandidates.filter((option) => {
      if (option.valueId === disallowedId) return false;
      if (!requiredGender) return true;
      return inferGender(option.person) === requiredGender;
    });
  }, [person2, spouseCandidates]);

  const spouseOptionsForPerson2 = useMemo(() => {
    if (!person1) return spouseCandidates;
    const disallowedId = person1.valueId;
    const requiredGender = getOppositeGender(person1);
    return spouseCandidates.filter((option) => {
      if (option.valueId === disallowedId) return false;
      if (!requiredGender) return true;
      return inferGender(option.person) === requiredGender;
    });
  }, [person1, spouseCandidates]);

  const { person1Label, person2Label, person1Options, person2Options } = useMemo(() => {
    switch (relationshipType) {
      case "Child":
        return {
          person1Label: "Select Child",
          person2Label: "Select Parent",
          person1Options: parentlessOptions,
          person2Options: coupleOptions,
        };
      case "Spouse":
        return {
          person1Label: "Select Spouse 1",
          person2Label: "Select Spouse 2",
          person1Options: spouseOptionsForPerson1,
          person2Options: spouseOptionsForPerson2,
        };
      case "Parent":
      default:
        return {
          person1Label: "Select Parent",
          person2Label: "Select Child",
          person1Options: coupleOptions,
          person2Options: parentlessOptions,
        };
    }
  }, [relationshipType, coupleOptions, parentlessOptions, spouseOptionsForPerson1, spouseOptionsForPerson2]);

  const activeRelationDetail = RELATION_DETAILS[relationshipType] || RELATION_DETAILS.Parent;
  const confirmationText = (() => {
    const fallback =
      activeRelationDetail.fallback ||
      activeRelationDetail.confirmation("the selected person", "the linked member");
    if (!person1 || !person2) return fallback;
    return activeRelationDetail.confirmation(person1.label, person2.label);
  })();

  const getNoOptionsText = (label) => `No ${label.toLowerCase()} found`;

  const handleClose = () => {
    setPerson1(null);
    setPerson2(null);
    setConfirmationChecked(false);
    setAttemptedSubmit(false);
    setSubmitting(false);
    setApiError("");
    if (onClose) onClose();
  };

  const handleContinue = async () => {
    setAttemptedSubmit(true);
    setApiError("");
    if (!person1 || !person2 || !confirmationChecked) return;

    const payload = {
      person1Id: person1?.valueId || "",
      person2Id: person2?.valueId || "",
      relation: RELATION_PAYLOAD_MAP[relationshipType] || "parent",
      action: "link",
    };

    try {
      setSubmitting(true);
      const response = await linkFamilyAPI(payload);
      if (response?.error) {
        setApiError(response.error || "Unable to link members");
        return;
      }
      if (typeof onLinkSuccess === "function") {
        try {
          await onLinkSuccess(response);
        } catch (callbackError) {
          console.error("Link success handler failed:", callbackError);
        }
      }
      handleClose();
    } catch (error) {
      setApiError(error?.message || "Unable to link members");
    } finally {
      setSubmitting(false);
    }
  };

  const getAutocompleteValue = (value, options) => {
    if (!value) return null;
    return options.find((opt) => opt.valueId === value.valueId) || value;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Existing Family Members</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={1} sx={{ maxWidth: 420, mx: "auto", width: "100%" }}>
          <Box>
            <Typography variant="caption" fontWeight={600} color="text.primary" display="block" mb={0.25}>
              Relationship Type <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField
              select
              required
              fullWidth
              size="small"
              value={relationshipType}
              onChange={(event) => setRelationshipType(event.target.value)}
              sx={FIELD_SX}
            >
              {RELATIONSHIP_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.primary" display="block" mb={0.25}>
              {person1Label} <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <Autocomplete
              options={person1Options}
              value={getAutocompleteValue(person1, person1Options)}
              onChange={(event, newValue) => setPerson1(newValue)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option?.valueId === value?.valueId}
              noOptionsText={getNoOptionsText(person1Label)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  placeholder={`Search ${person1Label.toLowerCase()}`}
                  error={attemptedSubmit && !person1}
                  helperText={attemptedSubmit && !person1 ? `${person1Label} is required` : null}
                  sx={FIELD_SX}
                />
              )}
            />
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.primary" display="block" mb={0.25}>
              {person2Label} <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <Autocomplete
              options={person2Options}
              value={getAutocompleteValue(person2, person2Options)}
              onChange={(event, newValue) => setPerson2(newValue)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option?.valueId === value?.valueId}
              noOptionsText={getNoOptionsText(person2Label)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  placeholder={`Search ${person2Label.toLowerCase()}`}
                  error={attemptedSubmit && !person2}
                  helperText={attemptedSubmit && !person2 ? `${person2Label} is required` : null}
                  sx={FIELD_SX}
                />
              )}
            />
          </Box>

          <FormControl required error={attemptedSubmit && !confirmationChecked} sx={{ mt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(event) => setConfirmationChecked(event.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography
                  component="span"
                  sx={{ fontSize: "0.9rem", color: "text.primary", display: "inline" }}
                >
                  {confirmationText}
                </Typography>
              }
              sx={{
                alignItems: "center",
                '& .MuiFormControlLabel-label': {
                  marginLeft: 0,
                  lineHeight: 1.3,
                },
              }}
            />
            {attemptedSubmit && !confirmationChecked && (
              <FormHelperText>Confirmation is required</FormHelperText>
            )}
          </FormControl>
          {apiError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {apiError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" color="inherit" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinue}
          disabled={submitting}
          sx={{ color: '#fff', '&.Mui-disabled': { color: '#fff', opacity: 0.5 } }}
        >
          {submitting ? "Linking..." : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExistingMemberLinkDialog;
